/**
 * Matrix credential cache, API fetch, token refresh, and token-expiry handling.
 */

import { captureChatException, captureChatMessage, addBreadcrumb } from 'shared/sentry';
import { isAbortError } from '../../utils/isAbortError.js';
import { getLocalItem, removeMultipleLocalItems, setMultipleLocalItems } from '../../utils/config/localStorage';
import { registerMatrix, refreshMatrixToken } from '../../actions/matrixAction';
import { AUTH_TOKEN_KEYS } from '../../constants/common';
import { isUserAdmin } from '../../utils/function';
import { MATRIX_LIST_SYNC_LIMIT } from './matrixConstants';
import { isHealthySyncState } from './matrixUtils';
import { shouldRefreshMatrixCredentials } from './matrixTokenFreshness';
import { isMatrixSyncStale } from './matrixTabResume';

const DEFAULT_MATRIX_INFO = {
  matrix_device_id: '',
  matrix_user_id: '',
  matrix_access_token: '',
};

let getUserInfoWarned = false;

/**
 * Persist Matrix credentials to localStorage so we don't re-register on every load.
 * @param {Object} data
 */
export function persistMatrixCredentials(data) {
  const token = data?.matrix_access_token;
  const userId = data?.matrix_user_id;
  const deviceId = data?.matrix_device_id;
  if (!token || !userId) return;

  setMultipleLocalItems?.({
    [AUTH_TOKEN_KEYS.MATRIX_ACCESS_TOKEN]: token,
    [AUTH_TOKEN_KEYS.MATRIX_USER_ID]: userId,
    [AUTH_TOKEN_KEYS.MATRIX_DEVICE_ID]: deviceId || '',
    [AUTH_TOKEN_KEYS.MATRIX_ACCESS_TOKEN_AT]: Date.now(),
  });
}

/** Clear Matrix credentials from localStorage to force re-register/refresh next time. */
export function clearMatrixCredentials() {
  removeMultipleLocalItems?.([
    AUTH_TOKEN_KEYS.MATRIX_ACCESS_TOKEN,
    AUTH_TOKEN_KEYS.MATRIX_USER_ID,
    AUTH_TOKEN_KEYS.MATRIX_DEVICE_ID,
    AUTH_TOKEN_KEYS.MATRIX_ACCESS_TOKEN_AT,
    AUTH_TOKEN_KEYS.MATRIX_INBOX_SNAPSHOT,
  ]);
}

/**
 * @returns {{
 *   success: true,
 *   userId: *,
 *   matrix_device_id: string,
 *   matrix_user_id: string,
 *   matrix_access_token: string
 * } | null}
 */
export function readCachedMatrixCredentials() {
  const cachedToken = getLocalItem(AUTH_TOKEN_KEYS.MATRIX_ACCESS_TOKEN);
  const cachedUserId = getLocalItem(AUTH_TOKEN_KEYS.MATRIX_USER_ID);
  const cachedDeviceId = getLocalItem(AUTH_TOKEN_KEYS.MATRIX_DEVICE_ID);
  if (!cachedToken || !cachedUserId) return null;
  return {
    success: true,
    userId: getLocalItem(AUTH_TOKEN_KEYS.USER_ID, null),
    ...DEFAULT_MATRIX_INFO,
    matrix_device_id: cachedDeviceId || '',
    matrix_user_id: cachedUserId,
    matrix_access_token: cachedToken,
  };
}

/**
 * Refresh/register Matrix credentials via backend when local cache is empty.
 * @returns {Promise<{ matrixUserInfo: Object, primaryError: *, fallbackError: * }>}
 */
export async function fetchMatrixCredentialsFromApi() {
  let matrixUserInfo = {
    userId: getLocalItem(AUTH_TOKEN_KEYS.USER_ID, null),
    ...DEFAULT_MATRIX_INFO,
  };
  let primaryError = null;
  let fallbackError = null;

  if (isUserAdmin()) {
    return { matrixUserInfo, primaryError, fallbackError };
  }

  try {
    let response = await refreshMatrixToken();
    if (!response.success) {
      primaryError = response?.error || response;
      response = await registerMatrix();
    }
    if (response.success) {
      matrixUserInfo.matrix_device_id = response.data.matrix_device_id;
      matrixUserInfo.matrix_user_id = response.data.matrix_user_id;
      matrixUserInfo.matrix_access_token = response.data.matrix_access_token;
      persistMatrixCredentials(response.data);
    } else {
      matrixUserInfo.matrix_device_id = '';
      matrixUserInfo.matrix_user_id = '';
      matrixUserInfo.matrix_access_token = '';
    }
  } catch (error) {
    primaryError = error;
    try {
      const registerResponse = await registerMatrix();
      if (registerResponse.success) {
        matrixUserInfo.matrix_device_id = registerResponse.data.matrix_device_id;
        matrixUserInfo.matrix_user_id = registerResponse.data.matrix_user_id;
        matrixUserInfo.matrix_access_token = registerResponse.data.matrix_access_token;
        persistMatrixCredentials(registerResponse.data);
      } else {
        matrixUserInfo.matrix_device_id = '';
        matrixUserInfo.matrix_user_id = '';
        matrixUserInfo.matrix_access_token = '';
      }
    } catch (registerError) {
      fallbackError = registerError;
      matrixUserInfo.matrix_device_id = '';
      matrixUserInfo.matrix_user_id = '';
      matrixUserInfo.matrix_access_token = '';
    }
  }
  return { matrixUserInfo, primaryError, fallbackError };
}

/**
 * Get user information from localStorage / token APIs.
 * Refreshes the short-lived Matrix access token when it is near the 6h expiry so
 * `startClient` does not long-poll `/sync` with `M_UNKNOWN_TOKEN`.
 * Long-idle CATCHUP is handled separately by dropping `since` (not by minting a token).
 * @param {{ forceRefresh?: boolean }} [options]
 * @returns {Promise<
 *   | { success: true, userId: *, matrix_device_id: string, matrix_user_id: string, matrix_access_token: string }
 *   | { success: false, reason: string, error: * }
 * >}
 */
export async function getUserInfo({ forceRefresh = false } = {}) {
  const cached = readCachedMatrixCredentials();
  const needsFreshToken = forceRefresh || shouldRefreshMatrixCredentials();

  if (cached && !needsFreshToken) return cached;

  const { matrixUserInfo, primaryError, fallbackError } = await fetchMatrixCredentialsFromApi();

  const err = primaryError ?? fallbackError ?? null;

  if (!matrixUserInfo.matrix_access_token || !matrixUserInfo.matrix_user_id) {
    if (!getUserInfoWarned) {
      getUserInfoWarned = true;
      captureChatMessage('Matrix getUserInfo failed; credentials unavailable', 'warning', {
        extra: { primaryError, fallbackError },
        tags: { matrix: 'auth' },
      });
    }
  }

  if (matrixUserInfo.matrix_access_token && matrixUserInfo.matrix_user_id) {
    return {
      success: true,
      ...matrixUserInfo,
    };
  }

  // Prefer a cached token over failing boot: 1:1 chat can still recover on 401.
  if (cached?.matrix_access_token && cached?.matrix_user_id) {
    return cached;
  }

  const reason = err ? 'TOKEN_FETCH_FAILED' : 'MISSING_CREDENTIALS';
  return { success: false, reason, error: err };
}

/**
 * @param {import('matrix-js-sdk').MatrixClient|null} matrixClient
 * @param {string} newAccessToken
 */
export function updateAccessToken(matrixClient, newAccessToken) {
  if (matrixClient && newAccessToken) {
    if (typeof matrixClient.setAccessToken === 'function') {
      matrixClient.setAccessToken(newAccessToken);
    } else if (matrixClient.credentials) {
      matrixClient.credentials.accessToken = newAccessToken;
    }
  }
}

/**
 * Check if error is a token expiration error (401)
 * @param {Error} error
 * @returns {boolean}
 */
export function isTokenExpiredError(error) {
  if (!error) return false;

  if (error.httpStatus === 401 || error.statusCode === 401 || error.status === 401) {
    return true;
  }

  if (error.response?.status === 401) {
    return true;
  }

  const errcode = error.errcode || error.data?.errcode || error.response?.data?.errcode;
  if (errcode === 'M_UNKNOWN_TOKEN') {
    return true;
  }

  const errorMessage = error.message || error.msg || error.response?.data?.error || '';
  const lowerMessage = errorMessage.toLowerCase();

  return (
    lowerMessage.includes('access token has expired') ||
    lowerMessage.includes('m_unknown_token') ||
    lowerMessage.includes('token expired') ||
    lowerMessage.includes('jwt expired') ||
    lowerMessage.includes('unauthorized') ||
    lowerMessage.includes('invalid token') ||
    lowerMessage.includes('session expired') ||
    lowerMessage.includes('login required')
  );
}

/**
 * Refresh Matrix token and update client.
 * `service` must expose: matrixClient, refreshPromise, updateAccessToken via updateAccessToken import,
 * persistMatrixCredentials, restartSync method.
 * @param {import('./matrixUtils').MatrixService} service
 * @param {{ freshInitialSync?: boolean, restartClient?: boolean }} [options]
 *   restartClient: default true after `M_UNKNOWN_TOKEN` so the failed long-poll is
 *   aborted (spec: retry with the new token). Set false for proactive rotate while
 *   still SYNCING — `setAccessToken` is enough; do not `stopClient` (drops VoIP
 *   `callEventHandler`). `restartSync` defers if a 1:1 call is in progress.
 *   freshInitialSync: omit `since` (CS API initial `/sync`). Only for long idle —
 *   never required just because the access token changed.
 * @returns {Promise<boolean>}
 */
export async function refreshTokenAndUpdateClient(service, options = {}) {
  const restartClient = options.restartClient !== false;
  const freshInitialSync = Boolean(options.freshInitialSync) || isMatrixSyncStale();

  const restartAfterNewToken = async (reason) => {
    if (!restartClient || !service.matrixClient) return;
    await service.restartSync({
      reason,
      force: true,
      minIntervalMs: 0,
      initialSyncLimit: MATRIX_LIST_SYNC_LIMIT,
      freshInitialSync,
    });
  };

  if (service.refreshPromise) {
    // Do not chain a second stop/start — that double-wipes the store and drops calls.
    return await service.refreshPromise;
  }

  if (isUserAdmin()) {
    return false;
  }

  service.refreshPromise = (async () => {
    try {
      let response = await refreshMatrixToken();

      if (!response.success) {
        response = await registerMatrix();
      }

      if (response.success && response.data?.matrix_access_token) {
        const newToken = response.data.matrix_access_token;
        updateAccessToken(service.matrixClient, newToken);
        persistMatrixCredentials(response.data);
        try {
          await restartAfterNewToken('post-refresh');
        } catch (e) {
          if (!isAbortError(e)) {
            captureChatException(e, { tags: { matrix: 'auth', action: 'sync-after-refresh' } });
          }
        }
        return true;
      }
      addBreadcrumb({
        category: 'matrix.auth',
        message: 'Matrix token refresh failed',
        level: 'warning',
        data: { error: response.error },
      });
      return false;
    } catch (error) {
      if (!isAbortError(error)) {
        captureChatException(error, { tags: { matrix: 'auth', action: 'token-refresh' } });
      }
      try {
        const registerResponse = await registerMatrix();
        if (registerResponse.success && registerResponse.data?.matrix_access_token) {
          const newToken = registerResponse.data.matrix_access_token;
          updateAccessToken(service.matrixClient, newToken);
          persistMatrixCredentials(registerResponse.data);
          try {
            await restartAfterNewToken('post-refresh-register');
          } catch (e) {
            if (!isAbortError(e)) {
              captureChatException(e, { tags: { matrix: 'auth', action: 'sync-after-register' } });
            }
          }
          return true;
        }
      } catch (registerError) {
        if (!isAbortError(registerError)) {
          captureChatException(registerError, { tags: { matrix: 'auth', action: 'register-fallback' } });
        }
      }
      return false;
    } finally {
      service.refreshPromise = null;
    }
  })();

  return await service.refreshPromise;
}

/**
 * Handle token-expiration in one place.
 * @param {import('./matrixUtils').MatrixService} service
 * @param {string} [source]
 * @param {*} [err]
 */
export async function handleTokenExpiry(service, source = 'unknown', err = null) {
  if (service._tokenExpiryHandling) return await service._tokenExpiryHandling;

  service._tokenExpiryHandling = (async () => {
    const now = Date.now();
    if (now - service.lastTokenToastAt > 60 * 1000) {
      try {
        service.lastTokenToastAt = now;
      } catch (_) {}
    }

    const refreshed = await refreshTokenAndUpdateClient(service, {
      // 401 during an active session: new token + incremental /sync (keep rooms).
      // Overnight idle: `restartSync` already upgrades to fresh initial /sync when stale.
      freshInitialSync: isMatrixSyncStale(),
    });
    if (refreshed) {
      try {
        const state = service.matrixClient?.getSyncState?.() || null;
        if (!isHealthySyncState(state)) {
          await service.requestSyncRecovery({
            reason: `token-refresh:${source}`,
            force: true,
            minIntervalMs: 0,
          });
        }
      } catch (e) {
        if (!isAbortError(e)) {
          captureChatException(e, { tags: { matrix: 'auth', action: 'sync-after-token-expiry' } });
        }
      }
      return true;
    }

    captureChatMessage('Matrix: unable to refresh token; stopping client', 'error', {
      extra: { err },
      tags: { matrix: 'auth' },
    });
    try {
      clearMatrixCredentials();
      service.reset();
      service._invokeAuthFailure({ reason: 'TOKEN_REFRESH_FAILED', source, error: err });
    } catch (_) {}

    return false;
  })();

  try {
    return await service._tokenExpiryHandling;
  } finally {
    service._tokenExpiryHandling = null;
  }
}
